import { formatAcres } from "./acreageFormatting.js";

export const EXCEL_ENTITY_LABELS = {
  farm: "Farm",
  field: "Field",
  machine: "Machine",
  fertilizer_ingredient: "Fertilizer Ingredient",
  fertilizer_mix: "Fertilizer Mix",
  crop_year: "Crop Year",
};

export const PREVIEW_STATUS_LABELS = {
  matched: "Matched",
  new: "New",
  ambiguous: "Ambiguous",
  missing_dependency: "Missing Dependency",
  invalid: "Invalid",
};

export const APPLY_OUTCOME_LABELS = {
  created: "Created",
  updated: "Updated",
  case_opened: "Needs Review",
  unchanged: "Unchanged",
  skipped_error: "Skipped",
};

export const RECONCILIATION_ACTIONS = {
  discard: "KEEP_CURRENT",
  update: "ACCEPT_INCOMING",
  override: "ACCEPT_ALL_INCOMING_FOR_RECORD",
};

export function makeExcelRowKey(row = {}) {
  return [
    row.sheet || "",
    row.row_index ?? "",
    row.entity_type || "",
  ].join("::");
}

export function groupExcelRowsBySheet(rows = []) {
  return rows.reduce((groups, row) => {
    const sheet = row?.sheet || "Workbook";
    if (!groups[sheet]) {
      groups[sheet] = [];
    }
    groups[sheet].push(row);
    return groups;
  }, {});
}

export function flattenDiffs(diffs = {}) {
  return Object.entries(diffs || {}).map(([fieldName, values]) => ({
    fieldName,
    current: values?.current,
    incoming: values?.incoming,
  }));
}

export function summarizePreviewRows(rows = []) {
  return rows.reduce(
    (summary, row) => {
      summary.total += 1;
      summary.statuses[row.match_status] =
        (summary.statuses[row.match_status] || 0) + 1;
      summary.errors += Array.isArray(row.errors) ? row.errors.length : 0;
      summary.warnings += Array.isArray(row.warnings) ? row.warnings.length : 0;
      summary.diffs += flattenDiffs(row.diffs).length;
      return summary;
    },
    {
      total: 0,
      statuses: {},
      errors: 0,
      warnings: 0,
      diffs: 0,
    }
  );
}

export function summarizeApplyResults(results = []) {
  return results.reduce(
    (summary, result) => {
      summary.total += 1;
      summary.outcomes[result.outcome] =
        (summary.outcomes[result.outcome] || 0) + 1;
      summary.errors += Array.isArray(result.errors) ? result.errors.length : 0;
      return summary;
    },
    {
      total: 0,
      outcomes: {},
      errors: 0,
    }
  );
}

export function pairApplyResultsWithPreview(applyResults = [], previewRows = []) {
  const previewByKey = new Map(
    previewRows.map((row) => [makeExcelRowKey(row), row])
  );

  return applyResults.map((result) => ({
    result,
    preview: previewByKey.get(makeExcelRowKey(result)) || null,
  }));
}

export function formatExcelValue(value) {
  if (value === null || value === undefined || value === "") {
    return "Blank";
  }

  if (typeof value === "object") {
    return JSON.stringify(value, null, 2);
  }

  return String(value);
}

export function formatExcelFieldValue(fieldName, value) {
  const normalizedFieldName = String(fieldName || "")
    .trim()
    .toLowerCase()
    .replaceAll(" ", "_");

  if (["acres", "field_size", "fieldsize"].includes(normalizedFieldName)) {
    return formatAcres(value, formatExcelValue(value));
  }

  return formatExcelValue(value);
}
